// CNC-inspired icon set — stroke-based, currentColor, matching the SnappedEndMill aesthetic.
// All icons share viewBox 0 0 24 24 (except decorative ones) and accept a `size` prop.

type IconProps = { size?: number; strokeWidth?: number; title?: string };

function Svg({
  size = 18,
  strokeWidth = 1.6,
  title,
  children,
}: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-label={title}
    >
      {children}
    </svg>
  );
}

/* End mill (intact) — for Equipment */
export function IconEndMill(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'End mill'}>
      <rect x="8" y="2" width="8" height="3" />
      <line x1="9.5" y1="5" x2="9.5" y2="6.5" />
      <line x1="12" y1="5" x2="12" y2="6.5" />
      <line x1="14.5" y1="5" x2="14.5" y2="6.5" />
      <rect x="10" y="6.5" width="4" height="4" />
      <rect x="9" y="10.5" width="6" height="9" />
      <path d="M9 12 Q12 13.5 15 12" />
      <path d="M9 14.5 Q12 16 15 14.5" />
      <path d="M9 17 Q12 18.5 15 17" />
      <path d="M9 19.5 L12 22 L15 19.5 Z" />
    </Svg>
  );
}

/* Caliper — for Technicians */
export function IconCaliper(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Caliper'}>
      <rect x="2" y="10" width="20" height="3" />
      <path d="M4 10 V6 H9 V10" />
      <path d="M14 13 V17 H19 V13" />
      <line x1="5" y1="10" x2="5" y2="7.5" />
      <line x1="6.5" y1="10" x2="6.5" y2="8" />
      <line x1="8" y1="10" x2="8" y2="7.5" />
      <line x1="15" y1="13" x2="15" y2="15.5" />
      <line x1="16.5" y1="13" x2="16.5" y2="15" />
      <line x1="18" y1="13" x2="18" y2="15.5" />
    </Svg>
  );
}

/* Filament spool — for Consumables */
export function IconSpool(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Spool'}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3" />
      <line x1="12" y1="3" x2="12" y2="9" />
      <line x1="12" y1="15" x2="12" y2="21" />
      <line x1="3" y1="12" x2="9" y2="12" />
      <line x1="15" y1="12" x2="21" y2="12" />
      <circle cx="12" cy="12" r="0.6" fill="currentColor" />
    </Svg>
  );
}

/* Clipboard with cut mark — for Orders */
export function IconClipboard(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Orders'}>
      <rect x="5" y="4" width="14" height="17" rx="1" />
      <rect x="9" y="2" width="6" height="3" />
      <line x1="8" y1="10" x2="16" y2="10" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="16" x2="13" y2="16" />
    </Svg>
  );
}

/* Customers — person in crosshair */
export function IconCustomer(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Customers'}>
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="2" x2="12" y2="5" />
      <line x1="12" y1="19" x2="12" y2="22" />
      <line x1="2" y1="12" x2="5" y2="12" />
      <line x1="19" y1="12" x2="22" y2="12" />
      <circle cx="12" cy="10" r="2" />
      <path d="M8 16 Q12 13 16 16" />
    </Svg>
  );
}

/* Statistics — bar chart with crosshair */
export function IconStats(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Statistics'}>
      <line x1="3" y1="21" x2="21" y2="21" />
      <rect x="5" y="14" width="3" height="7" />
      <rect x="10.5" y="9" width="3" height="12" />
      <rect x="16" y="5" width="3" height="16" />
      <line x1="3" y1="3" x2="5" y2="3" />
      <line x1="3" y1="3" x2="3" y2="5" />
    </Svg>
  );
}

/* Admin — gear + key */
export function IconAdmin(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Admin'}>
      <circle cx="9" cy="12" r="3.5" />
      <circle cx="9" cy="12" r="1" />
      <line x1="9" y1="5" x2="9" y2="7" />
      <line x1="9" y1="17" x2="9" y2="19" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="14" y1="12" x2="16" y2="12" />
      <line x1="4" y1="7" x2="5.5" y2="8.5" />
      <line x1="12.5" y1="15.5" x2="14" y2="17" />
      <line x1="4" y1="17" x2="5.5" y2="15.5" />
      <line x1="12.5" y1="8.5" x2="14" y2="7" />
      <line x1="16" y1="12" x2="20" y2="12" />
      <path d="M20 11 V13" />
      <path d="M18 11 V12.5" />
    </Svg>
  );
}

/* User Guide — manual with rivets */
export function IconManual(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'User Guide'}>
      <rect x="4" y="3" width="16" height="18" rx="1" />
      <circle cx="6.5" cy="5.5" r="0.5" fill="currentColor" />
      <circle cx="6.5" cy="18.5" r="0.5" fill="currentColor" />
      <circle cx="17.5" cy="5.5" r="0.5" fill="currentColor" />
      <circle cx="17.5" cy="18.5" r="0.5" fill="currentColor" />
      <line x1="8" y1="9" x2="16" y2="9" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="8" y1="15" x2="13" y2="15" />
    </Svg>
  );
}

/* Technical Docs — blueprint with gear */
export function IconBlueprint(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Technical Docs'}>
      <rect x="3" y="4" width="18" height="16" />
      <line x1="3" y1="8" x2="21" y2="8" />
      <line x1="7" y1="4" x2="7" y2="20" />
      <circle cx="14" cy="14" r="2.5" />
      <line x1="14" y1="10.5" x2="14" y2="12" />
      <line x1="14" y1="16" x2="14" y2="17.5" />
      <line x1="10.5" y1="14" x2="12" y2="14" />
      <line x1="16" y1="14" x2="17.5" y2="14" />
    </Svg>
  );
}

/* Settings — control dial */
export function IconDial(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Settings'}>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3" />
      <line x1="12" y1="4" x2="12" y2="6" />
      <line x1="12" y1="18" x2="12" y2="20" />
      <line x1="4" y1="12" x2="6" y2="12" />
      <line x1="18" y1="12" x2="20" y2="12" />
      <line x1="6.5" y1="6.5" x2="7.7" y2="7.7" />
      <line x1="16.3" y1="16.3" x2="17.5" y2="17.5" />
      <line x1="6.5" y1="17.5" x2="7.7" y2="16.3" />
      <line x1="16.3" y1="7.7" x2="17.5" y2="6.5" />
      <line x1="12" y1="12" x2="15" y2="9.5" />
    </Svg>
  );
}

/* Debug — broken bit with spark */
export function IconBrokenBit(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Debug'}>
      <rect x="9" y="2" width="6" height="2" />
      <rect x="10" y="4" width="4" height="3" />
      <path d="M10 7 L11 9 L13 7.5 L14 9" />
      <g transform="rotate(20 12 15)">
        <path d="M11 10 L13 10 L13.5 11.5 L10.5 11.5 Z" />
        <rect x="10.5" y="11.5" width="3" height="6" />
        <path d="M10.5 13 Q12 13.8 13.5 13" />
        <path d="M10.5 15 Q12 15.8 13.5 15" />
        <path d="M10.5 17.5 L12 19 L13.5 17.5 Z" />
      </g>
      <line x1="15" y1="7" x2="17" y2="6" />
      <line x1="16" y1="9" x2="18" y2="9" />
      <line x1="8" y1="8" x2="6" y2="7" />
    </Svg>
  );
}

/* Add — crosshair plus */
export function IconAdd(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Add'}>
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="7" x2="12" y2="17" />
      <line x1="7" y1="12" x2="17" y2="12" />
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22" y2="12" />
    </Svg>
  );
}

/* Delete — chip / swarf */
export function IconChip(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Delete'}>
      <path d="M4 7 L20 7" />
      <path d="M6 7 L7 20 H17 L18 7" />
      <path d="M9 4 H15 V7 H9 Z" />
      <path d="M10 11 L10 17" />
      <path d="M14 11 L14 17" />
    </Svg>
  );
}

/* Search — loupe with crosshair */
export function IconScope(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Search'}>
      <circle cx="10" cy="10" r="6" />
      <line x1="10" y1="7" x2="10" y2="13" />
      <line x1="7" y1="10" x2="13" y2="10" />
      <line x1="14.5" y1="14.5" x2="20" y2="20" />
    </Svg>
  );
}

/* Order Set-up — drafting triangle */
export function IconSetup(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Order set-up'}>
      <path d="M4 20 L20 20 L20 4 Z" />
      <line x1="8" y1="20" x2="8" y2="17" />
      <line x1="12" y1="20" x2="12" y2="15" />
      <line x1="16" y1="20" x2="16" y2="13" />
      <line x1="14" y1="8" x2="20" y2="8" />
    </Svg>
  );
}

/* In Quotation — document with dollar */
export function IconQuote(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Quotation'}>
      <path d="M6 3 H14 L18 7 V21 H6 Z" />
      <path d="M14 3 V7 H18" />
      <path d="M12 10 V18" />
      <path d="M14 12 H11 Q9.5 12 9.5 13.5 Q9.5 15 11 15 H13 Q14.5 15 14.5 16.5 Q14.5 18 13 18 H10" />
    </Svg>
  );
}

/* Supplying Consumables — package with arrow in */
export function IconSupply(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Supplying'}>
      <path d="M4 8 L12 4 L20 8 V18 L12 22 L4 18 Z" />
      <path d="M4 8 L12 12 L20 8" />
      <line x1="12" y1="12" x2="12" y2="22" />
      <line x1="8" y1="6" x2="16" y2="10" />
    </Svg>
  );
}

/* In Execution — rotating tool / gear with cut */
export function IconExecute(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'In execution'}>
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="4" x2="12" y2="7" />
      <line x1="12" y1="17" x2="12" y2="20" />
      <line x1="4" y1="12" x2="7" y2="12" />
      <line x1="17" y1="12" x2="20" y2="12" />
      <line x1="6" y1="6" x2="8" y2="8" />
      <line x1="16" y1="16" x2="18" y2="18" />
      <line x1="6" y1="18" x2="8" y2="16" />
      <line x1="16" y1="8" x2="18" y2="6" />
    </Svg>
  );
}

/* Preparing to Ship — box with tape */
export function IconPackage(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Preparing to ship'}>
      <rect x="4" y="7" width="16" height="13" />
      <line x1="4" y1="11" x2="20" y2="11" />
      <line x1="12" y1="7" x2="12" y2="20" />
      <path d="M8 7 L10 4 H14 L16 7" />
    </Svg>
  );
}

/* Shipped — truck */
export function IconTruck(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Shipped'}>
      <rect x="2" y="8" width="11" height="9" />
      <path d="M13 11 H18 L21 14 V17 H13 Z" />
      <circle cx="6" cy="19" r="2" />
      <circle cx="17" cy="19" r="2" />
      <line x1="2" y1="17" x2="4" y2="17" />
      <line x1="8" y1="17" x2="15" y2="17" />
      <line x1="19" y1="17" x2="21" y2="17" />
    </Svg>
  );
}

/* Delivered — check in target */
export function IconDelivered(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Delivered'}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12 L11 15 L16 9" />
    </Svg>
  );
}

/* Timer — stopwatch (Order Duration section) */
export function IconTimer(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Duration'}>
      <circle cx="12" cy="13" r="7" />
      <line x1="12" y1="6" x2="12" y2="4" />
      <line x1="10" y1="4" x2="14" y2="4" />
      <line x1="17" y1="8" x2="19" y2="6" />
      <line x1="12" y1="13" x2="15" y2="10" />
      <line x1="12" y1="13" x2="12" y2="9" />
      <line x1="5" y1="13" x2="6" y2="13" />
      <line x1="18" y1="13" x2="19" y2="13" />
    </Svg>
  );
}

/* Gantt — stacked bars (Order GANTT section) */
export function IconGantt(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Gantt'}>
      <line x1="3" y1="4" x2="3" y2="20" />
      <line x1="3" y1="20" x2="21" y2="20" />
      <rect x="5" y="6"  width="8"  height="2.5" fill="currentColor" opacity="0.2" />
      <rect x="5" y="6"  width="8"  height="2.5" />
      <rect x="9" y="10" width="10" height="2.5" fill="currentColor" opacity="0.2" />
      <rect x="9" y="10" width="10" height="2.5" />
      <rect x="7" y="14" width="6"  height="2.5" fill="currentColor" opacity="0.2" />
      <rect x="7" y="14" width="6"  height="2.5" />
    </Svg>
  );
}

/* Logout — door with arrow */
export function IconEject(p: IconProps) {
  return (
    <Svg {...p} title={p.title ?? 'Log out'}>
      <path d="M10 4 H5 V20 H10" />
      <line x1="9" y1="12" x2="20" y2="12" />
      <path d="M16 8 L20 12 L16 16" />
    </Svg>
  );
}
